import * as React from 'react';
import * as PropTypes from 'prop-types';
import { Fragment } from '../../utilities/components/Fragment';
import { AutoKeyFacet } from '../../models/AutoKeyFacet';
import { provideContext } from '../../utilities/components/Context';
import { IBackgroundTaskQueue, IBackgroundTask } from '../../models/backgroundTasks/IBackgroundTaskQueue';

/**
 * Represents the context for children which have access to a background task queue.
 * @internal
 */
export interface IBackgroundTaskContext {
    backgroundTaskQueue: IBackgroundTaskQueue;
}

/**
 * Context types for a background task host.
 * @internal
 */
export const BACKGROUND_TASK_CONTEXT_TYPES: React.ValidationMap<IBackgroundTaskContext> = {
    backgroundTaskQueue: PropTypes.object
};

/**
 * Component which provides a background task queue in context to all child elements.
 * @internal
 */
export const BackgroundTaskContextProvider = provideContext(BACKGROUND_TASK_CONTEXT_TYPES, (props: {
    queue: IBackgroundTaskQueue;
}): IBackgroundTaskContext => {
    return {
        backgroundTaskQueue: props.queue
    };
});

const backgroundTaskKeyFacet = new AutoKeyFacet('backgroundTask');

/**
 * @public
 */
export interface IBackgroundTaskHostProps {
    /**
     * A ref for the internal queue, once the host is mounted.
     */
    queueRef: (queue: IBackgroundTaskQueue | null) => void;
}

/**
 * @public
 */
export interface IBackgroundTaskHostState {
    tasks: {
        [key: string]: JSX.Element;
    };
}

/**
 * Component which renders a set of components comprising active background tasks.
 * These tasks are provided along with promises which represent their lifetimes.
 * Once their promises are resolved or rejected, the components are de-rendered.
 * @internal
 */
export class BackgroundTaskHost extends React.PureComponent<IBackgroundTaskHostProps, IBackgroundTaskHostState> {
    private _backgroundTaskQueue: IBackgroundTaskQueue;

    constructor(props: IBackgroundTaskHostProps) {
        super(props);

        this._backgroundTaskQueue = {
            enqueue: (task: IBackgroundTask) => this._enqueue(task)
        };

        this.state = {
            tasks: {}
        };
    }

    public componentDidMount(): void {
        this.props.queueRef(this._backgroundTaskQueue);
    }

    public render(): JSX.Element {
        return (
            <BackgroundTaskContextProvider queue={ this._backgroundTaskQueue }>
                <Fragment hidden={ true }>
                    {
                        Object.keys(this.state.tasks).map((key: string) => {
                            return (
                                <Fragment key={ key }>
                                    { this.state.tasks[key] }
                                </Fragment>
                            );
                        })
                    }
                </Fragment>
            </BackgroundTaskContextProvider>
        );
    }

    public componentWillUnmount(): void {
        this.props.queueRef(null);
    }

    private async _enqueue(task: IBackgroundTask): Promise<void> {
        const {
            content,
            promise
        } = task;

        const key = backgroundTaskKeyFacet.serializeNext();

        this.setState((previousState: IBackgroundTaskHostState): IBackgroundTaskHostState => {
            return {
                tasks: {
                    ...previousState.tasks,
                    [key]: content
                }
            };
        });

        try {
            await promise;
        } finally {
            this.setState((previousState: IBackgroundTaskHostState): IBackgroundTaskHostState => {
                const {
                    tasks: {
                        [key]: removedContent,
                        ...tasks
                    }
                } = previousState;

                return {
                    tasks: tasks
                };
            });
        }
    }
}

/**
 * Utility which buffers a series of queued background tasks until they can be handed to a proper queue.
 * @internal
 */
export class BackgroundTaskBuffer implements IBackgroundTaskQueue {
    private _earlyTasks: IBackgroundTask[];
    private _queue: IBackgroundTaskQueue | null;

    constructor() {
        this._earlyTasks = [];

        this._queue = null;
    }

    public enqueue = (task: IBackgroundTask): void => {
        const backgroundTaskQueue = this._queue;

        if (backgroundTaskQueue) {
            backgroundTaskQueue.enqueue(task);
        } else {
            this._earlyTasks.push(task);
        }
    }

    /**
     * A function to pass to BackgroundTaskHost so when it initializes, this queue
     * can be piped into its internal queue.
     */
    public queueRef = (backgroundTaskQueue: IBackgroundTaskQueue | null): void => {
        this._queue = backgroundTaskQueue;

        if (backgroundTaskQueue) {
            const tasks = this._earlyTasks.splice(0, Infinity);

            for (const task of tasks) {
                backgroundTaskQueue.enqueue(task);
            }
        }
    }
}
